import { Component } from "@theme/component";

class ProductUgcGallery extends Component {
  constructor() {
    super();
    this.blockId = null;
    this.modalId = null;
    this.carouselConfig = null;
    this.carouselSwiper = null;
    this.modalSwiper = null;
    this.clickedSlideIndex = 0;
    this.modalComponent = null;
    this.modalCarousel = null;
    this._swiperCheckInterval = null;
    this._modalSwiperCheckInterval = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.init();
  }

  updatedCallback() {
    super.updatedCallback();
    // Re-initialize when the component is updated by morph
    this.init();
  }

  init() {
    // Get block ID from data attribute
    this.blockId = this.dataset.blockId;
    if (!this.blockId) {
      return;
    }

    // Get modal ID
    this.modalId = "ugc-gallery-modal-" + this.blockId;

    // Find carousel element
    const carousel = this.querySelector('[data-config="productUgcGallery"]');
    if (!carousel) {
      return;
    }

    // Find modal component
    this.modalComponent = document.getElementById(this.modalId);
    if (!this.modalComponent) {
      return;
    }

    // Find modal carousel
    this.modalCarousel = document.getElementById("ugc-gallery-main-" + this.blockId);
    if (!this.modalCarousel) {
      return;
    }

    // Get carousel config from JSON script tag
    const configScript = this.querySelector("[data-carousel-config]");
    if (configScript) {
      try {
        this.carouselConfig = JSON.parse(configScript.textContent);
      } catch (e) {
        console.error("[Product UGC Gallery] Error parsing carousel config:", e);
        // Fallback to default config
        this.carouselConfig = {
          slidesPerView: 3.5,
          spaceBetween: 8,
          freeMode: true,
          scrollbar: {
            el: ".productUgcGallery__scrollbar",
            draggable: true,
            hide: false,
          },
          breakpoints: {
            764: {
              slidesPerView: 4,
            },
          },
        };
      }
    } else {
      // Fallback to default config if script tag not found
      this.carouselConfig = {
        slidesPerView: 3.5,
        spaceBetween: 8,
        freeMode: true,
        scrollbar: {
          el: ".productUgcGallery__scrollbar",
          draggable: true,
          hide: false,
        },
        breakpoints: {
          764: {
            slidesPerView: 4,
          },
        },
      };
    }

    // Initialize main carousel if Swiper is available
    if (typeof window.Swiper !== "undefined") {
      this.initCarousel();
    } else {
      // Wait for Swiper to load
      this._swiperCheckInterval = setInterval(() => {
        if (typeof window.Swiper !== "undefined") {
          clearInterval(this._swiperCheckInterval);
          this._swiperCheckInterval = null;
          this.initCarousel();
        }
      }, 100);
    }

    // Set up modal event listeners
    this.setupModalListeners();

    // Listen for featured review slide opening UGC modal (from product media gallery)
    this._onUgcGalleryOpen = () => {
      if (this.modalComponent && typeof this.modalComponent.showDialog === "function") {
        // No trigger image involved, so always open on the first slide
        this.clickedSlideIndex = 0;
        this.modalComponent.showDialog();
      }
    };
    document.addEventListener("ugc-gallery:open", this._onUgcGalleryOpen);
  }

  /**
   * A11y (Allyant #185): hide non-visible slides from screen readers and remove
   * their focusable elements from the tab order; restore both when a slide
   * scrolls into view. Relies on watchSlidesProgress → swiper-slide-visible.
   * @param {any} swiper
   */
  syncSlideA11y(swiper) {
    if (!swiper?.slides) return;
    for (const slide of swiper.slides) {
      const visible = slide.classList.contains("swiper-slide-visible");
      if (visible) {
        slide.removeAttribute("aria-hidden");
      } else {
        slide.setAttribute("aria-hidden", "true");
      }
      for (const el of slide.querySelectorAll("button, a, [tabindex]")) {
        if (visible) {
          el.removeAttribute("tabindex");
        } else {
          el.setAttribute("tabindex", "-1");
        }
      }
    }
  }

  /**
   * A11y (Allyant #188): defensively remove Swiper's injected live-region
   * notification (aria-live) — a11y module is disabled in config, this guards
   * against stale cached configs.
   * @param {HTMLElement} root
   */
  stripSwiperLiveRegion(root) {
    if (!root) return;
    for (const el of root.querySelectorAll(".swiper-notification, [aria-live]")) {
      el.remove();
    }
  }

  /**
   * Shared a11y enforcement merged into every Swiper init in this component:
   * no Swiper a11y module (it injects the flagged aria-live node), visible-slide
   * tracking for #185, and visibility sync bound to every movement source
   * (buttons, drag, scrollbar, resize).
   * @returns {any}
   */
  a11yConfigOverrides() {
    const sync = swiper => {
      this.syncSlideA11y(swiper);
      this.stripSwiperLiveRegion(swiper.el);
    };
    return {
      a11y: { enabled: false },
      watchSlidesProgress: true,
      on: {
        afterInit: sync,
        slideChange: sync,
        transitionEnd: sync,
        resize: sync,
        touchEnd: sync,
        scrollbarDragEnd: sync,
      },
    };
  }

  /**
   * Initialize the main carousel
   */
  initCarousel() {
    if (!this.carouselConfig || typeof window.Swiper === "undefined") {
      return;
    }

    const carousel = this.querySelector('[data-config="productUgcGallery"]');
    if (!carousel) {
      return;
    }

    // Destroy existing Swiper if it exists
    if (carousel.swiper) {
      try {
        carousel.swiper.destroy(true, true);
      } catch (e) {
        console.warn("[Product UGC Gallery] Error destroying carousel Swiper:", e);
      }
    }

    // Applied here rather than in each config source (JSON block or either
    // fallback) so every path gets the same accessibility behavior.
    // scrollOnFocus brings a focused off-screen thumbnail into view.
    this.carouselConfig.watchSlidesProgress = true;
    this.carouselConfig.a11y = {
      enabled: true,
      scrollOnFocus: true,
      // Labelled landmark on the carousel container (WCAG 1.3.1, 4.1.2)
      containerRole: "region",
      containerMessage: "Customer photo carousel",
      slideRole: null,
      slideLabelMessage: null,
      wrapperLiveRegion: false,
      ...(this.carouselConfig.a11y || {}),
    };

    // Dragging and the draggable scrollbar are the only ways to move this
    // carousel, neither of which a keyboard can do. Swiper's keyboard module
    // gives it arrow-key control (WCAG 2.1.1). Set here for the same reason as
    // the a11y block above; a config source can still override it.
    this.carouselConfig.keyboard = {
      enabled: true,
      onlyInViewport: true,
      // Page Up / Page Down are how keyboard users scroll the page - leave them alone.
      pageUpDown: false,
      ...(this.carouselConfig.keyboard || {}),
    };

    // Initialize Swiper
    try {
      this.carouselSwiper = new window.Swiper(carousel, {
        ...this.carouselConfig,
        ...this.a11yConfigOverrides(),
      });
    } catch (e) {
      console.error("[Product UGC Gallery] Error initializing carousel Swiper:", e);
    }
  }

  /**
   * Resolve the slide index from the element that opened the modal.
   *
   * @param {Element} [triggerElement] - The element that triggered the dialog open
   */
  setClickedSlideIndex(triggerElement) {
    const button = triggerElement?.closest?.(".product__ugcGalleryItemButton[data-slide-index]");
    if (!button) {
      return;
    }

    const slideIndex = parseInt(button.getAttribute("data-slide-index"), 10);
    if (!isNaN(slideIndex)) {
      this.clickedSlideIndex = slideIndex;
    }
  }

  /**
   * Set up modal event listeners
   */
  setupModalListeners() {
    if (!this.modalComponent) {
      return;
    }

    // Listen for modal open event
    this.modalComponent.addEventListener("dialog:open", event => {
      // `dialog:open` is dispatched synchronously from the click that opened the
      // modal, so read the trigger from the event instead of a separate click
      // listener — the index has to be known before Swiper initializes.
      this.setClickedSlideIndex(event.detail?.triggerElement);
      // Swiper's keyboard module listens on the document and only checks that its
      // carousel is in the viewport, which the strip behind the dialog still is.
      // Left unhandled, one arrow key press would move both carousels at once.
      this.carouselSwiper?.keyboard?.disable();
      this.initModalCarousel();
    });

    // Clean up Swiper when modal closes
    this.modalComponent.addEventListener("dialog:close", () => {
      this.destroyModalCarousel();
      this.carouselSwiper?.keyboard?.enable();
    });
  }

  /**
   * Initialize Swiper in modal when modal opens.
   *
   * Runs synchronously from `dialog:open` — the dialog is already displayed by
   * then, so slides can be measured, and the very first painted frame has the
   * correct gaps and starts on the clicked slide. Deferring this (even by a
   * frame) reflows the carousel while the open animation is still running,
   * which reads as a jerky slide-in on mobile.
   */
  initModalCarousel() {
    if (!this.modalCarousel) {
      return;
    }

    if (typeof window.Swiper === "undefined") {
      // Swiper comes from a deferred CDN script — retry until it lands
      if (!this._modalSwiperCheckInterval) {
        this._modalSwiperCheckInterval = setInterval(() => {
          if (typeof window.Swiper === "undefined") {
            return;
          }
          this.clearModalSwiperCheck();
          this.initModalCarousel();
        }, 50);
      }
      return;
    }

    this.clearModalSwiperCheck();

    // Destroy existing Swiper if it exists
    if (this.modalSwiper) {
      this.modalSwiper.destroy(true, true);
      this.modalSwiper = null;
    }

    const dialogEl = this.modalComponent?.querySelector("dialog");
    const configId = dialogEl?.dataset.modalConfigId || `ugc_modal_${this.blockId.replace(/-/g, "_")}`;
    const paginationRoot = `[data-carousel-pagination='${configId}']`;

    // Remove swiper-initialized class if it exists
    this.modalCarousel.classList.remove("swiper-initialized");
    try {
      this.modalSwiper = new window.Swiper(this.modalCarousel, {
        slidesPerView: "auto",
        spaceBetween: 16,
        // Trailing space matching the modal's left padding (px-4 / md:p-8)
        slidesOffsetAfter: 16,
        initialSlide: this.clickedSlideIndex,
        watchOverflow: true,
        observer: true,
          watchSlidesProgress: true,
          a11y: {
            enabled: true,
            scrollOnFocus: true,
            // Labelled landmark on the carousel container (WCAG 1.3.1, 4.1.2)
            containerRole: "region",
            containerMessage: "Customer photo gallery carousel",
            slideRole: null,
            slideLabelMessage: null,
            wrapperLiveRegion: false,
          },
          // Nothing inside these slides is focusable, so arrow keys are the only
          // way a keyboard reaches the images past the first one (WCAG 2.1.1).
          keyboard: {
            enabled: true,
            onlyInViewport: true,
            pageUpDown: false,
          },
        navigation: {
          nextEl: `${paginationRoot} .nav-next`,
          prevEl: `${paginationRoot} .nav-prev`,
        },
        pagination: {
          el: `${paginationRoot} .pagination`,
          clickable: true,
          type: "bullets",
          renderBullet: (idx, className) => {
            const isActive = className.includes("swiper-pagination-bullet-active");
            return `<button type="button" class="${className}" aria-label="Slide ${idx + 1}" aria-current="${isActive ? "true" : "false"}" tabindex="${isActive ? "0" : "-1"}"><span class="progress-bar"></span></button>`;
          },
        },
        breakpoints: {
          768: {
            spaceBetween: 20,
            slidesOffsetAfter: 32,
          },
          1280: {
            spaceBetween: 24,
            slidesOffsetAfter: 32,
          },
        },
        on: {
          init: swiper => {
            if (typeof window.attachPaginationHandlers === "function") {
              window.attachPaginationHandlers(swiper, `${paginationRoot} .pagination`);
            }
          },
        },
            ...this.a11yConfigOverrides(),
      });
    } catch (e) {
      console.error("[Product UGC Gallery] Error initializing modal Swiper:", e);
    }
  }

  /**
   * Clear the pending Swiper availability check for the modal carousel
   */
  clearModalSwiperCheck() {
    if (this._modalSwiperCheckInterval) {
      clearInterval(this._modalSwiperCheckInterval);
      this._modalSwiperCheckInterval = null;
    }
  }

  /**
   * Destroy modal Swiper when modal closes
   */
  destroyModalCarousel() {
    this.clearModalSwiperCheck();

    if (this.modalSwiper) {
      try {
        this.modalSwiper.destroy(true, true);
        this.modalSwiper = null;
      } catch (e) {
        console.warn("[Product UGC Gallery] Error destroying modal Swiper:", e);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Remove ugc-gallery:open listener
    if (this._onUgcGalleryOpen) {
      document.removeEventListener("ugc-gallery:open", this._onUgcGalleryOpen);
      this._onUgcGalleryOpen = null;
    }
    // Clear Swiper check interval if it exists
    if (this._swiperCheckInterval) {
      clearInterval(this._swiperCheckInterval);
      this._swiperCheckInterval = null;
    }
    // Clean up Swipers
    if (this.carouselSwiper) {
      try {
        this.carouselSwiper.destroy(true, true);
      } catch (error) {
        console.warn("[Product UGC Gallery] Error destroying carousel Swiper during cleanup:", error);
      }
    }
    this.destroyModalCarousel();
  }
}

if (!customElements.get("product-ugc-gallery")) {
  customElements.define("product-ugc-gallery", ProductUgcGallery);
}
